import { BehaviorSubject, Observable } from 'rxjs';
import { ArnAuthStatus, ArnConnectionStatus } from './ArnAuthStatus';
import { ArnClientError } from '../ArnClientError';
import {
  AnyArianeeJWT,
  ArnConnector,
  ArnConnectorType,
  Base64Util,
  JWTHeader,
  ObjectUtil,
} from '@arianee/arn-types';
import { ethers, TypedDataDomain, TypedDataField } from 'ethers';
import { ArnLogger } from '../log';

export class NoAuthContextError extends ArnClientError {
  constructor() {
    super('No auth context');
  }
}

export class AlreadyConnectedError extends ArnClientError {
  constructor() {
    super('Already connected');
  }
}

export class ArnAuthContext {
  static readonly AAT_JWT_HEADER: JWTHeader = { typ: 'JWT', alg: 'ETH' };

  static readonly AUTH_CACHE_KEY = 'arianee-auth-';

  protected _status$: BehaviorSubject<ArnAuthStatus>;

  get status$(): Observable<ArnAuthStatus> {
    return this._status$;
  }

  static readonly DEFAULT_STATUS: ArnAuthStatus = {
    connectionStatus: ArnConnectionStatus.disconnected,
    address: undefined,
    signature: undefined,
    aat: undefined,
    connectorType: ArnConnectorType.Web3Modal,
    connectorOpts: undefined,
  };

  protected static n = 0;

  protected readonly log: ArnLogger;

  constructor(
    log: ArnLogger,
    protected connector: ArnConnector,
    protected _status = ArnAuthContext.DEFAULT_STATUS,
    protected persist = true
  ) {
    ArnAuthContext.n++;
    this.log = log.fork('context-' + ArnAuthContext.n);
    this._status$ = new BehaviorSubject<ArnAuthStatus>(_status);
    this.status = _status;
  }

  // #region Connector

  /**
   * Ask the wallet to sign an AAT.
   *
   * @param jwtPayload The AAT JWT Payload.
   * @param message?? (optional) The message to sign.
   * @param sep?? Signature separator string.
   * @param jwtHeader? The AAT JWT header.
   */
  async signAAT(
    jwtPayload: AnyArianeeJWT,
    message?: string,
    sep?: string,
    jwtHeader: JWTHeader = ArnAuthContext.AAT_JWT_HEADER
  ): Promise<string> {
    const headerAsString = JSON.stringify(jwtHeader);
    const payloadAsString = JSON.stringify(jwtPayload);
    let toSign = `${headerAsString}.${payloadAsString}`;
    let toBuf = headerAsString;
    if (message) {
      const messageWithSep = sep ? message + sep : message;
      toSign = messageWithSep + toSign;
      toBuf = messageWithSep + toBuf;
    }
    const payloadB64 = Base64Util.toBase64(payloadAsString);
    const fingerprint = `${Base64Util.toBase64(toBuf)}.${payloadB64}`;
    const signature = await this.signMessage(toSign);
    return `${fingerprint}.${signature}`;
  }

  /**
   * Request the user to sign a message using his wallet.
   * @param message The message to sign
   * @return The signature of the message (hex string)
   */
  async signMessage(message: string) {
    return this.connector.signMessage(message);
  }

  /**
   * Request the user to sign typed data using his wallet.
   * @see https://eips.ethereum.org/EIPS/eip-712
   * @return The signature of the typed data (hex string)
   */
  async signTypedData(
    domain: TypedDataDomain,
    types: Record<string, Array<TypedDataField>>,
    value: Record<string, any>
  ) {
    return this.connector.signTypedData(domain, types, value);
  }

  /**
   * Request the user to sign a transaction using his wallet.
   * @param transaction The transaction to sign
   * @return The signed transaction (hex string)
   */
  async signTransaction(
    transaction: ethers.utils.Deferrable<ethers.providers.TransactionRequest>
  ) {
    return this.connector.signTransaction(transaction);
  }

  /**
   * Request the user to send a transaction using his wallet.
   * @param transaction The transaction to send
   * @return The transaction response (not the receipt)
   */
  async sendTransaction(
    transaction: ethers.utils.Deferrable<ethers.providers.TransactionRequest>
  ) {
    return this.connector.sendTransaction(transaction);
  }

  /**
   * Request the user to send a transaction using his wallet.
   * @param transaction The transaction to send
   * @return The transaction hash (hex string)
   */
  async sendUncheckedTransaction(
    transaction: ethers.utils.Deferrable<ethers.providers.TransactionRequest>
  ) {
    return this.connector.sendUncheckedTransaction(transaction);
  }

  /**
   * Request the user's chain ID.
   */
  async getChainId() {
    return this.connector.getChainId();
  }

  /**
   * Request the user to switch to a specific chain.
   * @param chainId The chain ID to switch to
   */
  async switchChain(chainId: number) {
    this.log.debug('switchChain', chainId);
    await this.connector.switchChain(chainId);
  }

  /**
   * Disconnect from the current connector
   */
  async disconnect() {
    this.log.debug('disconnect');
    await this.connector.disconnect();
    this.status = {
      ...this._status,
      connectionStatus: ArnConnectionStatus.disconnected,
      address: undefined,
      signature: undefined,
      aat: undefined,
    };
  }

  // #endregion

  set status(newStatus: ArnAuthStatus) {
    this.log.debug('set status', newStatus);
    this._status = newStatus;
    if (this.persist) {
      localStorage.setItem(
        ArnAuthContext.AUTH_CACHE_KEY + newStatus.connectorType,
        JSON.stringify(newStatus)
      );
    }
    this._status$.next(newStatus);
  }

  get status() {
    return this._status;
  }

  static asSet(context: ArnAuthContext | undefined): ArnAuthContext {
    return ObjectUtil.asSet(context, () => new NoAuthContextError());
  }

  static asConnected(context: ArnAuthContext | undefined): ArnAuthContext {
    const ctx = ArnAuthContext.asSet(context);
    if (!ctx.isConnected()) {
      throw new ArnClientError('Not connected');
    }
    return ctx;
  }

  static asDisconnected(
    context: ArnAuthContext | undefined,
    connectedError = () => new AlreadyConnectedError()
  ): ArnAuthContext | undefined {
    if (context?.isConnected()) {
      throw connectedError(); // TODO: Support multiple different connections
    }
    return context;
  }

  readonly isConnected = () =>
    this._status.connectionStatus !== ArnConnectionStatus.disconnected;
}
